import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Briefcase, PhoneCall, Plus } from 'lucide-react';
import { useModalLayer } from '@/lib/layerManager';

interface NewActionButtonProps {
  canCreateJob: boolean;
  canCall: boolean;
  onNewJob: () => void;
  onNewCall: () => void;
}

/**
 * Round "+" button at the bottom of the phone/tablet sidebar. With both actions allowed it opens a small
 * menu above itself (New Job / New Call); with only one allowed it goes straight to that one.
 */
export function NewActionButton({ canCreateJob, canCall, onNewJob, onNewCall }: NewActionButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const open = anchor !== null;
  const close = () => setAnchor(null);
  // No back-navigation entry: picking an option immediately opens a modal that pushes its own.
  const layer = useModalLayer({ enabled: open, onClose: close, backNavigation: false });

  const handleClick = () => {
    if (canCreateJob && !canCall) return onNewJob();
    if (canCall && !canCreateJob) return onNewCall();
    if (open) return close();
    if (buttonRef.current) setAnchor(buttonRef.current.getBoundingClientRect());
  };

  const pick = (action: () => void) => {
    close();
    action();
  };

  const options = [
    ...(canCreateJob ? [{ label: 'New Job', icon: Briefcase, color: 'text-orange-500', action: onNewJob }] : []),
    ...(canCall ? [{ label: 'New Call', icon: PhoneCall, color: 'text-amber-500', action: onNewCall }] : []),
  ];

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        aria-label="Create new"
        aria-expanded={open}
        className="flex h-11 w-11 items-center justify-center rounded-full text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 shadow-lg shadow-primary-500/30 transition-all duration-200"
      >
        <Plus className={`h-6 w-6 transition-transform duration-200 ${open ? 'rotate-45' : ''}`} />
      </button>

      {open && createPortal(
        <div className="fixed inset-0" style={layer.style} onClick={layer.onClose}>
          <div
            className="fixed w-48 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl"
            style={{ left: anchor.left, bottom: window.innerHeight - anchor.top + 8 }}
            onClick={e => e.stopPropagation()}
          >
            {options.map(({ label, icon: Icon, color, action }) => (
              <button
                key={label}
                type="button"
                onClick={() => pick(action)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <Icon className={`h-5 w-5 ${color}`} />
                {label}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
